
import type { LoginResponse, Role } from '../../types/Login/auth.types';

const TOKEN_KEY = 'token';
const EMPLOYEE_ID_KEY = 'employeeId';
const EMPLOYEE_CODE_KEY = 'employeeCode';
const EMPLOYEE_NAME_KEY = 'employeeName';
const ROLES_KEY = 'roles';
const ROLE_KEY = 'role';
const ROLE_ID_KEY = 'roleId';

export const clearAuthStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMPLOYEE_ID_KEY);
  localStorage.removeItem(EMPLOYEE_CODE_KEY);
  localStorage.removeItem(EMPLOYEE_NAME_KEY);
  localStorage.removeItem(ROLES_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(ROLE_ID_KEY);
  localStorage.removeItem('rolePermissions');
};

const normalizeRoles = (roles: Role[] | undefined): Role[] => {
  if (!Array.isArray(roles)) {
    return [];
  }

  const validRoles = roles.filter(
    (role) =>
      Number.isInteger(role?.roleId) &&
      role.roleId > 0 &&
      typeof role.roleName === 'string' &&
      role.roleName.trim().length > 0,
  );

  return Array.from(
    new Map(
      validRoles.map((role) => [
        role.roleId,
        { roleId: role.roleId, roleName: role.roleName.trim() },
      ]),
    ).values(),
  );
};

const saveLoginData = (data: LoginResponse) => {
  clearAuthStorage();

  const roles = normalizeRoles(data.roles);

  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(EMPLOYEE_ID_KEY, String(data.employeeId));
  localStorage.setItem(EMPLOYEE_CODE_KEY, data.employeeCode);
  localStorage.setItem(EMPLOYEE_NAME_KEY, data.employeeName);
  localStorage.setItem(ROLES_KEY, JSON.stringify(roles));

  // Prefer Employee for the initial dashboard when assigned.
  // Do not invent a role ID if Employee is absent from the API response.
  const initialRole =
    roles.find((role) => role.roleName.toLowerCase() === 'employee') ??
    roles[0];

  if (initialRole) {
    localStorage.setItem(ROLE_KEY, initialRole.roleName);
    localStorage.setItem(ROLE_ID_KEY, String(initialRole.roleId));
  } else {
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(ROLE_ID_KEY);
  }

  window.dispatchEvent(new Event('workvista-auth-updated'));
};

export const saveEmployeeLogin = (data: LoginResponse) => {
  saveLoginData(data);
};

export const saveSsoLogin = (data: LoginResponse) => {
  saveLoginData(data);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getRole = () =>
  localStorage.getItem(ROLE_KEY) ?? 'Employee';

export const getRoleId = (): number | null => {
  const value = localStorage.getItem(ROLE_ID_KEY);

  if (!value) {
    return null;
  }

  const roleId = Number(value);

  return Number.isInteger(roleId) && roleId > 0 ? roleId : null;
};

export const getEmployeeId = () => localStorage.getItem(EMPLOYEE_ID_KEY);

export const getEmployeeCode = () =>
  localStorage.getItem(EMPLOYEE_CODE_KEY) ?? '';

export const getEmployeeName = () =>
  localStorage.getItem(EMPLOYEE_NAME_KEY) ?? 'User';

export const getRoles = (): Role[] => {
  try {
    const value = localStorage.getItem(ROLES_KEY);

    if (!value) {
      return [];
    }

    return normalizeRoles(JSON.parse(value) as Role[]);
  } catch {
    return [];
  }
};

export const getManagerEmployeeCode = () => getEmployeeCode();

export const isAuthenticated = () => Boolean(getToken());
